import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { FabricContext } from '@context/FabricContext';
import { useSelectedMVComment } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { hasRenderableCommentAnnotations } from '@pages/MediaViewer/helpers/comment-annotations';

export default function useHasAnnotations() {
  const selectedComment = useSelectedMVComment();
  const { selectedCommentId, canvasMode, selectedVersion } =
    useAssetViewerState();
  const { canvas, getObjects } = useContext(FabricContext);

  const isDraftComment =
    selectedCommentId === 'new' || selectedCommentId === 'reply';
  const useCanvasAsSource = isDraftComment || canvasMode === 'edit';
  const sourceKey = useCanvasAsSource
    ? `canvas:${selectedCommentId ?? 'none'}:${canvasMode}`
    : `comment:${selectedCommentId ?? 'none'}`;
  const [state, setState] = useState({ sourceKey, has: false });
  const previousSourceKeyRef = useRef(sourceKey);
  const awaitingDraftCanvasClearRef = useRef(false);

  if (sourceKey !== previousSourceKeyRef.current) {
    const wasViewingPersistedComment =
      previousSourceKeyRef.current.startsWith('comment:') &&
      previousSourceKeyRef.current !== 'comment:none';
    awaitingDraftCanvasClearRef.current =
      isDraftComment && wasViewingPersistedComment && getObjects().length > 0;
    previousSourceKeyRef.current = sourceKey;
  }

  const setHasAnnotations = useCallback(
    (has: boolean) => {
      setState((current) =>
        current.sourceKey === sourceKey && current.has === has
          ? current
          : { sourceKey, has }
      );
    },
    [sourceKey]
  );

  const syncHasAnnotations = useCallback(() => {
    if (awaitingDraftCanvasClearRef.current) {
      setHasAnnotations(false);
      return;
    }

    if (getObjects().length > 0) {
      setHasAnnotations(true);
      return;
    }

    if (useCanvasAsSource) {
      setHasAnnotations(false);
      return;
    }

    if (
      selectedComment &&
      hasRenderableCommentAnnotations(
        selectedComment,
        selectedVersion?.metadata.type
      )
    ) {
      setHasAnnotations(true);
      return;
    }
    setHasAnnotations(false);
  }, [
    getObjects,
    selectedComment,
    selectedVersion,
    setHasAnnotations,
    useCanvasAsSource
  ]);

  useEffect(() => {
    syncHasAnnotations();
  }, [syncHasAnnotations]);

  useLayoutEffect(() => {
    if (!canvas) return;
    const onCanvasCleared = () => {
      awaitingDraftCanvasClearRef.current = false;
      setHasAnnotations(false);
    };
    syncHasAnnotations();
    canvas.on('canvas:cleared', onCanvasCleared);
    canvas.on('object:history:add', syncHasAnnotations);
    canvas.on('object:history:remove', syncHasAnnotations);
    return () => {
      canvas.off('canvas:cleared', onCanvasCleared);
      canvas.off('object:history:remove', syncHasAnnotations);
      canvas.off('object:history:add', syncHasAnnotations);
    };
  }, [canvas, setHasAnnotations, syncHasAnnotations]);

  return state.sourceKey === sourceKey ? state.has : false;
}
